import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import { RecordManagement } from "app/class/record-management";
import SysRecord from "app/class/record";
import { normalizeGateWidgetDefinition } from "modules/gate/types/gate";
import useNotification from "component/global/event-handler/hooks/use-notification";
import {
    ModalMessageInput,
    ModalOpenInput,
    ModalProgressInput,
    ModalResult,
    registerModalRuntime,
} from "./modalService";
import { GateWidgetModalFrame } from "./GateWidgetModalFrame";

type ModalRequest =
    | {
        type: "widget";
        input: ModalOpenInput;
        resolve: (result: ModalResult) => void;
        reject: (error: Error) => void;
    }
    | {
        type: "alert" | "confirm";
        input: ModalMessageInput;
        resolve: (result: ModalResult) => void;
        reject: (error: Error) => void;
    }
    | {
        type: "progress";
        input: ModalProgressInput;
        resolve: (result: ModalResult) => void;
        reject: (error: Error) => void;
    };

interface ModalRuntimeProviderProps {
    children: ReactNode;
}

const sizeToMaxWidth = (size?: ModalOpenInput["size"]) => {
    switch (size) {
        case "sm": return "sm";
        case "lg": return "lg";
        case "xl":
        case "fullscreen": return "xl";
        case "md":
        default: return "md";
    }
};

export const ModalRuntimeProvider = ({ children }: ModalRuntimeProviderProps) => {
    const [request, setRequest] = useState<ModalRequest | null>(null);
    const [widget, setWidget] = useState<ReturnType<typeof normalizeGateWidgetDefinition> | null>(null);
    const [loadingWidget, setLoadingWidget] = useState(false);
    const requestRef = useRef<ModalRequest | null>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const notification = useNotification();
    const { t } = useTranslation();

    const openRequest = useCallback((nextRequest: ModalRequest) => {
        if (requestRef.current) {
            return Promise.reject(new Error("Another modal is already open"));
        }

        openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        return new Promise<ModalResult>((resolve, reject) => {
            nextRequest.resolve = resolve;
            nextRequest.reject = reject;
            requestRef.current = nextRequest;
            setRequest(nextRequest);
        });
    }, []);

    const closeWithResolve = useCallback((result: ModalResult) => {
        const current = requestRef.current;
        requestRef.current = null;
        setRequest(null);
        setWidget(null);
        current?.resolve(result);
        requestAnimationFrame(() => openerRef.current?.focus());
    }, []);

    const openProgress = useCallback((input: ModalProgressInput) => {
        if (requestRef.current) {
            throw new Error("Another modal is already open");
        }

        let resolveClosed: (result: ModalResult) => void = () => undefined;
        const closed = new Promise<ModalResult>((resolve) => {
            resolveClosed = resolve;
        });

        const progressRequest: ModalRequest = {
            type: "progress",
            input,
            resolve: resolveClosed,
            reject: () => undefined,
        };

        requestRef.current = progressRequest;
        setRequest(progressRequest);

        return {
            closed,
            update: (nextInput: Partial<ModalProgressInput>) => {
                setRequest((current) => {
                    if (!current || current.type !== "progress") return current;

                    const updatedRequest = {
                        ...current,
                        input: {
                            ...current.input,
                            ...nextInput,
                        },
                    };

                    requestRef.current = updatedRequest;
                    return updatedRequest;
                });
            },
            close: (result: ModalResult = { action: "close" }) => {
                closeWithResolve(result);
            },
        };
    }, [closeWithResolve]);

    useEffect(() => registerModalRuntime({
        open: (input) => openRequest({
            type: "widget",
            input,
            resolve: () => undefined,
            reject: () => undefined,
        }),
        alert: (input) => openRequest({
            type: "alert",
            input,
            resolve: () => undefined,
            reject: () => undefined,
        }),
        confirm: (input) => openRequest({
            type: "confirm",
            input,
            resolve: () => undefined,
            reject: () => undefined,
        }),
        progress: openProgress,
    }), [openProgress, openRequest]);

    useEffect(() => {
        if (!request || request.type !== "widget") {
            setWidget(null);
            return;
        }

        let cancelled = false;
        setLoadingWidget(true);

        resolveWidget(request.input)
            .then((resolvedWidget) => {
                if (cancelled) return;
                setWidget(resolvedWidget);
            })
            .catch((error) => {
                if (cancelled) return;
                closeWithReject(error instanceof Error ? error : new Error("Unable to load modal widget"));
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingWidget(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [request]);

    const closeWithReject = useCallback((error: Error) => {
        const current = requestRef.current;
        requestRef.current = null;
        setRequest(null);
        setWidget(null);
        current?.reject(error);
    }, []);

    const notify = useCallback((message: string, level = "info") => {
        const normalizedLevel = ["success", "error", "warning", "info"].includes(level)
            ? level as "success" | "error" | "warning" | "info"
            : "info";

        notification[normalizedLevel === "error" ? "alert" : normalizedLevel]("modal-gate-notify", message);
    }, [notification]);

    const dialogTitle = useMemo(() => {
        if (!request) return "";
        return t(request.input.title || (request.type === "confirm" ? "Confirm" : request.type === "alert" ? "Information" : widget?.name || "Modal"));
    }, [request, t, widget?.name]);

    const fullScreen = request?.type === "widget" && request.input.size === "fullscreen";
    const open = Boolean(request);
    const disableClose = request?.type === "progress" && request.input.disableClose;

    return (
        <>
            {children}
            <Dialog
                open={open}
                PaperProps={{ "aria-modal": "true" }}
                onClose={() => {
                    if (!disableClose) closeWithResolve({ action: "close" });
                }}
                fullWidth
                fullScreen={fullScreen}
                maxWidth={request?.type === "widget" ? sizeToMaxWidth(request.input.size) : "sm"}
            >
                {dialogTitle && <DialogTitle>{dialogTitle}</DialogTitle>}
                <DialogContent
                    dividers={request?.type === "widget"}
                    sx={request?.type === "widget" ? { height: fullScreen ? "100%" : "70vh", p: 0 } : undefined}
                >
                    {request?.type === "alert" && (
                        <Typography>{t(request.input.message || "")}</Typography>
                    )}
                    {request?.type === "confirm" && (
                        <Typography>{t(request.input.message || "")}</Typography>
                    )}
                    {request?.type === "widget" && loadingWidget && (
                        <Typography sx={{ p: 2 }}>{t("Loading modal...")}</Typography>
                    )}
                    {request?.type === "widget" && widget && (
                        <GateWidgetModalFrame
                            widget={widget}
                            data={request.input.data}
                            notify={notify}
                            onResolve={closeWithResolve}
                            onReject={closeWithReject}
                        />
                    )}
                    {request?.type === "progress" && (
                        <ProgressModalContent input={request.input} />
                    )}
                </DialogContent>
                {request && (request.type === "alert" || request.type === "confirm" || (request.type === "progress" && !request.input.disableClose)) && (
                    <DialogActions>
                        {request.type === "confirm" && (
                            <Button onClick={() => closeWithResolve({ action: "cancel" })}>
                                {t(request.input.cancelText || "Cancel")}
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={() => closeWithResolve({ action: "confirm" })}
                        >
                            {t(request.type === "progress" ? request.input.closeText || "Close" : request.input.confirmText || "OK")}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        </>
    );
};

const ProgressModalContent = ({ input }: { input: ModalProgressInput }) => {
    const { t } = useTranslation();
    const status = input.status || "loading";
    const normalizedValue = typeof input.value === "number"
        ? Math.min(100, Math.max(0, input.value))
        : undefined;

    return (
        <Stack spacing={2.5} sx={{ py: 1 }}>
            <Box>
                <Typography variant="body1" fontWeight={600}>
                    {t(input.message || "Processing...")}
                </Typography>
                {input.detail && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t(input.detail)}
                    </Typography>
                )}
            </Box>
            <LinearProgress
                color={status === "error" ? "error" : status === "success" ? "success" : "primary"}
                variant={normalizedValue === undefined ? "indeterminate" : "determinate"}
                value={normalizedValue}
                sx={{ height: 8, borderRadius: 999 }}
            />
            {input.steps?.length ? (
                <Stack spacing={1}>
                    {input.steps.map((step, index) => {
                        const activeStep = input.activeStep ?? 0;
                        const isDone = index < activeStep || status === "success";
                        const isActive = index === activeStep && status === "loading";
                        const color = status === "error" && index === activeStep
                            ? "error.main"
                            : isDone || isActive
                                ? "primary.main"
                                : "text.secondary";

                        return (
                            <Stack key={`${step}-${index}`} direction="row" spacing={1.25} alignItems="center">
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: color,
                                        flex: "0 0 auto",
                                    }}
                                />
                                <Typography variant="body2" color={color} fontWeight={isActive ? 600 : 400}>
                                    {t(step)}
                                </Typography>
                            </Stack>
                        );
                    })}
                </Stack>
            ) : null}
        </Stack>
    );
};

const resolveWidget = async (input: ModalOpenInput) => {
    const identifier = input.widgetId || input.widget;

    if (!identifier) {
        throw new Error("Modal widget is required");
    }

    const manager = new RecordManagement<SysRecord>("sys_gate_widgets");

    if (input.widgetId) {
        const record = await manager.get(input.widgetId);
        return normalizeGateWidgetDefinition(record);
    }

    const response = await manager
        .where("active", true)
        .where("code", identifier)
        .orWhere("name", identifier)
        .run();

    const record = response.data?.[0];

    if (!record) {
        throw new Error(`Gate widget not found: ${identifier}`);
    }

    return normalizeGateWidgetDefinition(record);
};
